import { Router } from 'express';
import { db } from '../storage';
import { ScheduleSession } from '../../src/types';

export const scheduleRouter = Router();

// Get sessions for teacher
scheduleRouter.get('/', (req, res) => {
  const teacherId = req.query.teacherId as string;
  const month = req.query.month as string;
  if (!teacherId) return res.status(400).json({ error: 'Missing teacherId' });

  const sessions = db.getSessions(teacherId, month);
  res.json(sessions);
});

// Create single session
scheduleRouter.post('/', (req, res) => {
  const {
    teacherId,
    classId,
    date,
    startTime,
    endTime,
    location,
    topic,
    homework,
    notes,
    status
  } = req.body;

  if (!teacherId || !classId || !date || !startTime || !endTime) {
    return res.status(400).json({ error: 'Thiếu thông tin ngày giờ hoặc lớp học' });
  }

  const cls = db.getClassById(teacherId, classId);
  const d = new Date(date);
  const dayOfWeek = d.getDay();

  const newSession: ScheduleSession = {
    id: `sess_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    teacherId,
    classId,
    className: cls ? cls.name : 'Lớp học',
    classColor: cls ? cls.color : '#059669',
    date,
    dayOfWeek,
    startTime,
    endTime,
    location: location || (cls ? cls.roomOrLink : 'Phòng học'),
    status: status || 'scheduled',
    topic: topic || '',
    homework: homework || '',
    notes: notes || '',
    actualStudentsPresent: 0,
    earnedAmount: cls ? cls.pricePerSession : 350000,
    reminderSentZalo: false,
    createdAt: new Date().toISOString()
  };

  db.createSession(newSession);
  res.json(newSession);
});

// Auto-generate sessions for a custom date range (e.g. from date to date, all days or selected days)
scheduleRouter.post('/generate-range', (req, res) => {
  const { teacherId, startDate, endDate, classId, daysOfWeek, startTime, endTime, location, topic } = req.body;
  if (!teacherId || !startDate || !endDate) {
    return res.status(400).json({ error: 'Vui lòng cung cấp ngày bắt đầu và ngày kết thúc' });
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  if (start > end) {
    return res.status(400).json({ error: 'Ngày bắt đầu không được lớn hơn ngày kết thúc' });
  }

  const classes = db.getClasses(teacherId).filter(c => c.active);
  const targetClasses = classId && classId !== 'all' ? classes.filter(c => c.id === classId) : classes;
  if (targetClasses.length === 0) {
    return res.status(400).json({ error: 'Không tìm thấy lớp học phù hợp' });
  }

  const existingSessions = db.getSessions(teacherId);
  const existingMap = new Set(existingSessions.map(s => `${s.classId}_${s.date}_${s.startTime}`));
  const createdSessions: ScheduleSession[] = [];

  const cur = new Date(start);
  while (cur <= end) {
    const dayOfWeek = cur.getDay(); // 0=CN, 1=T2...
    const year = cur.getFullYear();
    const month = cur.getMonth() + 1;
    const day = cur.getDate();
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    for (const c of targetClasses) {
      // If user specified custom time / days
      if (startTime && endTime) {
        // Check if daysOfWeek filter matches (if provided)
        const matchDay = !daysOfWeek || daysOfWeek.length === 0 || daysOfWeek.includes(dayOfWeek);
        if (matchDay) {
          const key = `${c.id}_${dateStr}_${startTime}`;
          if (!existingMap.has(key)) {
            const sess: ScheduleSession = {
              id: `sess_${c.id}_${dateStr}_${startTime.replace(':', '')}_${Math.random().toString(36).substring(2, 5)}`,
              teacherId,
              classId: c.id,
              className: c.name,
              classColor: c.color,
              date: dateStr,
              dayOfWeek,
              startTime,
              endTime,
              location: location || c.roomOrLink || 'Phòng học',
              status: 'scheduled',
              topic: topic || `Buổi học môn ${c.subject}`,
              homework: '',
              notes: '',
              actualStudentsPresent: 0,
              earnedAmount: c.pricePerSession,
              reminderSentZalo: false,
              createdAt: new Date().toISOString()
            };
            db.createSession(sess);
            createdSessions.push(sess);
            existingMap.add(key);
          }
        }
      } else {
        // Use class schedule template
        const matchTemplates = c.scheduleTemplate.filter(t => t.dayOfWeek === dayOfWeek);
        for (const t of matchTemplates) {
          const key = `${c.id}_${dateStr}_${t.startTime}`;
          if (!existingMap.has(key)) {
            const sess: ScheduleSession = {
              id: `sess_${c.id}_${dateStr}_${t.startTime.replace(':', '')}_${Math.random().toString(36).substring(2, 5)}`,
              teacherId,
              classId: c.id,
              className: c.name,
              classColor: c.color,
              date: dateStr,
              dayOfWeek,
              startTime: t.startTime,
              endTime: t.endTime,
              location: t.location || c.roomOrLink,
              status: 'scheduled',
              topic: `Buổi học định kỳ môn ${c.subject}`,
              homework: '',
              notes: '',
              actualStudentsPresent: 0,
              earnedAmount: c.pricePerSession,
              reminderSentZalo: false,
              createdAt: new Date().toISOString()
            };
            db.createSession(sess);
            createdSessions.push(sess);
            existingMap.add(key);
          }
        }
      }
    }
    cur.setDate(cur.getDate() + 1);
  }

  res.json({
    success: true,
    generatedCount: createdSessions.length,
    message: `Đã tạo thành công ${createdSessions.length} buổi học từ ${startDate} đến ${endDate}!`
  });
});

// Auto-generate sessions for a selected month based on class schedule templates
scheduleRouter.post('/generate-monthly', (req, res) => {
  const { teacherId, year, month } = req.body; // month is 1-12
  if (!teacherId || !year || !month) {
    return res.status(400).json({ error: 'Missing teacherId, year or month' });
  }

  const classes = db.getClasses(teacherId).filter(c => c.active);
  const daysInMonth = new Date(year, month, 0).getDate();
  const createdSessions: ScheduleSession[] = [];

  const existingSessions = db.getSessions(teacherId, `${year}-${String(month).padStart(2, '0')}`);
  const existingMap = new Set(existingSessions.map(s => `${s.classId}_${s.date}_${s.startTime}`));

  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month - 1, day);
    const dayOfWeek = d.getDay();
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    for (const c of classes) {
      const matchTemplates = c.scheduleTemplate.filter(t => t.dayOfWeek === dayOfWeek);
      for (const t of matchTemplates) {
        const key = `${c.id}_${dateStr}_${t.startTime}`;
        if (!existingMap.has(key)) {
          const sess: ScheduleSession = {
            id: `sess_${c.id}_${dateStr}_${t.startTime.replace(':', '')}`,
            teacherId,
            classId: c.id,
            className: c.name,
            classColor: c.color,
            date: dateStr,
            dayOfWeek,
            startTime: t.startTime,
            endTime: t.endTime,
            location: t.location || c.roomOrLink,
            status: 'scheduled',
            topic: `Buổi học định kỳ môn ${c.subject}`,
            homework: '',
            notes: '',
            actualStudentsPresent: 0,
            earnedAmount: c.pricePerSession,
            reminderSentZalo: false,
            createdAt: new Date().toISOString()
          };
          db.createSession(sess);
          createdSessions.push(sess);
          existingMap.add(key);
        }
      }
    }
  }

  res.json({
    success: true,
    generatedCount: createdSessions.length,
    message: `Đã tự động tạo ${createdSessions.length} buổi dạy trong tháng ${month}/${year}`
  });
});

// Reschedule / Move / Makeup session
scheduleRouter.post('/reschedule', (req, res) => {
  const { teacherId, sessionId, newDate, newTime, reason } = req.body;
  if (!teacherId || !sessionId || !newDate || !newTime) {
    return res.status(400).json({ error: 'Vui lòng cung cấp đầy đủ thông tin ngày mới và giờ mới' });
  }

  const updated = db.rescheduleSession(teacherId, sessionId, newDate, newTime, reason || '');
  if (!updated) {
    return res.status(404).json({ error: 'Không tìm thấy buổi học để đổi lịch' });
  }

  res.json({
    success: true,
    message: 'Đã dời lịch buổi học thành công!',
    session: updated
  });
});

// Update session (e.g. mark completed, change time, add homework, mark Zalo sent)
scheduleRouter.put('/:id', (req, res) => {
  const { id } = req.params;
  const { teacherId, ...updates } = req.body;
  if (!teacherId) return res.status(400).json({ error: 'Missing teacherId' });

  // If marked completed, compute earnings
  if (updates.status === 'completed' && updates.actualStudentsPresent === undefined) {
    const studentsInClass = db.getStudents(teacherId, updates.classId);
    updates.actualStudentsPresent = studentsInClass.length > 0 ? studentsInClass.length : 1;
  }

  const updated = db.updateSession(teacherId, id, updates);
  if (!updated) return res.status(404).json({ error: 'Không tìm thấy buổi học' });
  res.json(updated);
});

// Delete session
scheduleRouter.delete('/:id', (req, res) => {
  const { id } = req.params;
  const teacherId = req.query.teacherId as string;
  if (!teacherId) return res.status(400).json({ error: 'Missing teacherId' });

  const success = db.deleteSession(teacherId, id);
  res.json({ success });
});
